/**
 * Copyright 2023 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import React, { useEffect, useMemo, useRef } from 'react';
import type {
  ContentStackBanner,
  ContentStackBannerSplit,
  ContentStackImage,
  SizePerDevice,
} from '../../interface';
import { Legal } from '@/components/AZCustomComponent/Legal';
import styles from './CMSBanner.module.scss';
import { mapContentstackLegalValues } from '@/utils/mapContentstackLegalValues';
import cx from 'classnames';
import { CMSImage } from '../CMSImage/CMSImage';
import { reportCitrusClick } from '@/features/citrus/utils/reportCitrusClick';
import { reportCitrusImpression } from '@/features/citrus/utils/reportCitrusImpression';
import { CMSLink } from '../CMSLink/CMSLink';
import { CMSHeadline } from '../CMSHeadline/CMSHeadline';
import { isLegalDefined } from '../../utils/isLegalDefined';

export type CMSBannerProps = {
  content: ContentStackBanner;
  dropShadowSize?: ContentStackBannerSplit['drop_shadow'];
  sponsored?: boolean;
  citrusInfo?: {
    citrusAdId: string;
  };
  legalPadding?:
    | boolean
    | {
        s?: boolean;
        m?: boolean;
        l?: boolean;
      };
  priority?: boolean;
  sizePerDevice?: SizePerDevice;
  loading?: 'lazy' | 'eager';
};

const EXPECTED_HOSTS = ['autozone.com'];

const defaultSizePerDevice = {
  mobile: {
    width: 1376,
    height: 550,
  },
  tablet: {
    width: 2220,
    height: 275,
  },
  desktop: {
    width: 2332,
    height: 270,
  },
};

function prependWwwIfMissing(url: string): string {
  const trimmedUrl = url.trim();

  if (!trimmedUrl) {
    return trimmedUrl;
  }

  try {
    const urlWithProtocol = /^https?:\/\//i.test(trimmedUrl) ? trimmedUrl : `https://${trimmedUrl}`;

    const parsedUrl = new URL(urlWithProtocol);
    const hostname = parsedUrl.hostname.toLowerCase();

    parsedUrl.protocol = 'https:';

    if (EXPECTED_HOSTS.includes(hostname)) {
      parsedUrl.hostname = `www.${hostname}`;
    }

    return parsedUrl.toString();
  } catch {
    return url;
  }
}

function normalizeBannerImageUrls(image: ContentStackImage): ContentStackImage {
  return {
    ...image,
    image_url: image.image_url ? prependWwwIfMissing(image.image_url) : image.image_url,
    attributes: image.attributes?.map((attribute) => ({
      ...attribute,
      device_image: prependWwwIfMissing(attribute.device_image),
    })),
  };
}

export function CMSBanner({
  content,
  dropShadowSize,
  sponsored,
  citrusInfo,
  legalPadding,
  priority,
  sizePerDevice,
  loading,
}: CMSBannerProps) {
  const { image, legal, link, asset_size, heading, heading_underline } = content;
  const imageRef = useRef<HTMLImageElement>(null);

  const normalizedImage = useMemo(
    () => (image ? normalizeBannerImageUrls(image) : undefined),
    [image],
  );

  const legalLinkAriaLabel =
    legal?.aria_label || `${legal?.link_name} about ${normalizedImage?.image_alt_text}`;

  useEffect(() => {
    if (!sponsored) {
      return;
    }

    const currentImageRef = imageRef.current;

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && citrusInfo?.citrusAdId) {
        void reportCitrusImpression(citrusInfo.citrusAdId);
      }
    }, {});

    if (currentImageRef) {
      observer.observe(currentImageRef);
    }

    return () => {
      if (currentImageRef) {
        observer.unobserve(currentImageRef);
      }
    };
  }, [sponsored, citrusInfo?.citrusAdId]);

  return (
    <>
      {heading && (
        <CMSHeadline isBannerHeadline underline={Boolean(heading_underline)}>
          {heading}
        </CMSHeadline>
      )}

      {normalizedImage &&
        (link ? (
          <CMSLink
            to={link}
            className={styles.bannerLink}
            samePage
            onClick={() => {
              if (citrusInfo?.citrusAdId) {
                void reportCitrusClick(citrusInfo.citrusAdId);
              }
            }}
            onAuxClick={(event) => {
              if (event.button === 1 && citrusInfo?.citrusAdId) {
                void reportCitrusClick(citrusInfo.citrusAdId);
              }
            }}
            ariaLabel={normalizedImage.image_alt_text}
          >
            <CMSImage
              ref={imageRef}
              image={normalizedImage}
              loading={loading}
              sizePerDevice={sizePerDevice ?? defaultSizePerDevice}
              priority={priority}
              className={cx(
                styles.bannerImage,
                dropShadowSize && styles[`dropShadow${dropShadowSize}`],
                asset_size && !sponsored && styles[`assetSize${asset_size}`],
              )}
            />
          </CMSLink>
        ) : (
          <CMSImage
            ref={imageRef}
            image={normalizedImage}
            loading={loading}
            sizePerDevice={sizePerDevice ?? defaultSizePerDevice}
            priority={priority}
            className={cx(
              styles.bannerImage,
              dropShadowSize && styles[`dropShadow${dropShadowSize}`],
              asset_size && styles[`assetSize${asset_size}`],
            )}
          />
        ))}

      {!!legal && (isLegalDefined(legal) || sponsored) && (
        <div
          className={cx({
            [styles.legalPaddingS]:
              (typeof legalPadding === 'object' && legalPadding.s) ||
              (typeof legalPadding === 'boolean' && !!legalPadding),
            [styles.legalPaddingM]:
              (typeof legalPadding === 'object' && legalPadding.m) ||
              (typeof legalPadding === 'boolean' && !!legalPadding),
            [styles.legalPaddingL]:
              (typeof legalPadding === 'object' && legalPadding.l) ||
              (typeof legalPadding === 'boolean' && !!legalPadding),
          })}
        >
          <Legal
            content={{
              ...mapContentstackLegalValues(legal),
              ariaLabel: legalLinkAriaLabel,
            }}
            isQuotient={sponsored}
          />
        </div>
      )}
    </>
  );
}
